/* eslint-disable sonarjs/cognitive-complexity */
import moment from 'moment';
import { useEffect, useCallback, useState, useRef } from 'react';
import { MdInfo, MdOutlineFileDownload } from 'react-icons/md';
import { FaCheckCircle } from 'react-icons/fa';
import { useSelector, useDispatch } from 'react-redux';
import { useDebounce } from 'use-debounce';
import Button from 'components/Button';
import Chevron from 'components/Chevron';
import CircleIcon from 'components/CircleIcon';
import Modal from 'components/Modal';
import Spinner from 'components/Spinner';
import useWhiteLabelSettings from 'hooks/useWhiteLabelSettings';
import {
  setIsCardHolderDetailsOpen,
  setPaymentInputValue,
  setSelectedPaymentType,
} from 'reducers/paymentForm/paymentFormSlice';
import {
  getPaymentDetails,
  getPaymentHistory,
} from 'services/CommonService';
import { DEFAULT_PRIMARY_COLOUR } from 'shared/constants/styleConstants';
import { displayAmount } from 'shared/helpers/common';
import NextPaymentDates from './NextPaymentDates';

export default function MakeAPayment({ refreshBookingAfterPayment }) {
  const dateFormat = 'DD/MM/YYYY';
  const dispatch = useDispatch();
  const booking = useSelector((state) => state.ui.selectedBooking);
  const { isCardHolderDetailsOpen, paymentInputValue, selectedPaymentType } = useSelector(
    (state) => state.paymentForm,
  );
  const { componentConfig, textConfig = {} } = useSelector(
    (state) => state.ui,
  );
  const { reference, balanceDueDate, due, currency } = booking;
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 471);
  const [isTablet, setIsTablet] = useState(window.innerWidth < 768);
  const [isXL, setIsXL] = useState(window.innerWidth < 1280);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [paymentFormLoadCount, setPaymentFormLoadCount] = useState(0);
  const [paymentInputError, setpaymentInputError] = useState('');
  const [cardInputError, setcardInputError] = useState('');
  const [isPaymentFormModalOpen, setPaymentFormModal] = useState(false);
  const [isPaymentHistoryModalOpen, setPaymentHistoryModal] = useState(false);
  const [paymentHistory, setPaymentHistoryData] = useState(null);
  const [isPaymentInputValid, setIsPaymentInputValid] = useState(true);
  const [isCardTypeValid, setIsCardTypeValid] = useState(true);
  const [isPaymentInProgress, setIsPaymentInProgress] = useState(false);
  const [debouncedPaymentInputValue] = useDebounce(paymentInputValue, 1000);
  const [isPageReady, setIsPageReady] = useState(false);
  const ref = useRef();

  const { whiteLabelSettings = {} } = useWhiteLabelSettings();
  const { primaryColour } = whiteLabelSettings;

  const loadData = useCallback(async () => {
    try {
      const { data: paymentDetailsData } = await getPaymentDetails(reference);
      const initialPaymentInputValue = due;

      setPaymentDetails(paymentDetailsData);
      dispatch(setPaymentInputValue(initialPaymentInputValue));

      if (paymentDetailsData.paymentTypes.length === 1) {
        dispatch(setSelectedPaymentType(paymentDetailsData.paymentTypes[0]));
      }

      if (componentConfig.bookingReceiptsEnabled) {
        const { data: paymentHistoryData } = await getPaymentHistory(reference);
        setPaymentHistoryData(paymentHistoryData);
      }

      setTimeout(() => {
        setIsPageReady(true);
      }, 500);
    } catch (error) {
      console.error(error);
    }
  }, [
    reference,
    setPaymentDetails,
    setPaymentHistoryData,
    dispatch,
    due,
    componentConfig.bookingReceiptsEnabled,
  ]);

  const onWindowResize = () => {
    setIsMobile(window.innerWidth < 471);
    setIsTablet(window.innerWidth < 768);
    setIsXL(window.innerWidth < 1280);
  };

  const isPaymentDue = useCallback(() => {
    return moment().isAfter(balanceDueDate);
  }, [balanceDueDate]);

  useEffect(() => {
    loadData();
    setIsAccordionOpen(isPaymentDue());
    dispatch(setPaymentInputValue(due));
  }, [loadData, isPaymentDue, dispatch, due]);

  useEffect(() => {
    window.addEventListener('resize', onWindowResize);
    return () => window.removeEventListener('resize', onWindowResize);
  }, []);

  useEffect(() => {
    if (!isPaymentInProgress) {
      dispatch(setPaymentInputValue(due));
    }
  }, [due, isPaymentInProgress, dispatch]);

  const getCurrencySymbol = (currencyCode) => {
    try {
      return new Intl.NumberFormat('en', {
        style: 'currency',
        currency: currencyCode,
      }).formatToParts(0)[0].value;
    } catch (error) {
      console.error('Error getting currency symbol:', error);
      return currencyCode;
    }
  };

  const getHeaderText = () => {
    if (selectedPaymentType?.card) return 'Card Holder Details';
    if (isCardHolderDetailsOpen) return 'Payment Details';
    return 'Payment Form';
  };

  let chevronDirection;
  if (isMobile) {
    chevronDirection = 'right';
  } else {
    chevronDirection = isAccordionOpen ? 'down' : 'up';
  }

  const paymentDueStyling = { fontSize: '28px', fontWeight: '500' };

  const containerStyle = `mt-[40px] mb-20 flex flex-wrap justify-center md:justify-between text-center lg:text-left lg:mb-0 ${isAccordionOpen ? 'h-auto' : 'h-10'}`;

  const accordionButtonStyle = isAccordionOpen
    ? 'w-full md:w-auto rounded-t-xl rounded-b-none origin-top transition-all'
    : 'w-full md:w-auto rounded-xl';

    const accordionCommonStyles =
    'md:border-2 md:border-primary md:rounded-xl md:rounded-tl-none md:origin-top md:transition-all md:duration-500 md:ease-in-out flex-col flex bg-white lg:w-full overflow-hidden';

  const accordionTabletStyles = isTablet
    ? 'w-screen h-screen fixed top-0 left-0 bg-white z-10 items-baseline'
    : '';

  const accordionStyle = isAccordionOpen
    ? `${accordionTabletStyles} ${accordionCommonStyles} max-h-[1000px] opacity-100 scale-y-100`
    : `${accordionCommonStyles} max-h-0 opacity-0 scale-y-95`;

  const colCss = 'min-w-full text-left md:min-w-[20%]';

  const tdCss = 'flex justify-between pb-2 xl:justify-start xl:pb-0';

  const tdStatusDocs = 'flex justify-center pt-2 pb-2 xl:justify-start xl:pt-0 xl:pb-0'

  function toggleAccordionOpen() {
    setIsAccordionOpen(!isAccordionOpen);
  }

  const toggleCardHolderDetailsForm = useCallback(() => {
    dispatch(setIsCardHolderDetailsOpen(!isCardHolderDetailsOpen));
  }, [dispatch, isCardHolderDetailsOpen]);

  const handlePaymentInputChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      dispatch(setPaymentInputValue(newValue));
    },
    [dispatch],
  );

  useEffect(() => {
    dispatch(setIsCardHolderDetailsOpen(false));
    setPaymentFormLoadCount(0);
  }, [dispatch]);

  useEffect(() => {
    const checkWithinPaymentMinMax = () => {
      const numericValue = parseFloat(debouncedPaymentInputValue);
      const decimalCount =
        typeof debouncedPaymentInputValue === 'string' &&
        debouncedPaymentInputValue.includes('.')
          ? debouncedPaymentInputValue.split('.')[1].length
          : 0;

      if (
        !Number.isNaN(numericValue) &&
        numericValue >= paymentDetails.minimumPayment &&
        numericValue <= paymentDetails.maximumPayment
      ) {
        let formattedValue = '';

        if (
          typeof debouncedPaymentInputValue === 'string' &&
          (decimalCount === 1 ||
            (decimalCount === 2 && debouncedPaymentInputValue.endsWith('0')) ||
            debouncedPaymentInputValue.endsWith('.'))
        ) {
          formattedValue = isPageReady ? debouncedPaymentInputValue : due;
        } else {
          formattedValue = numericValue.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            useGrouping: false,
          });
        }
        dispatch(setPaymentInputValue(formattedValue));
        setpaymentInputError(
          `Minimum payment amount ${displayAmount(
            paymentDetails.minimumPayment,
            currency,
          )}`,
        );
        setIsPaymentInputValid(true);
      } else {
        setpaymentInputError(
          `Payment value must be above ${displayAmount(
            paymentDetails.minimumPayment,
            currency,
          )} and up to ${displayAmount(
            paymentDetails.maximumPayment,
            currency,
          )}.`,
        );
        setIsPaymentInputValid(false);
      }
    };

    const checkCardType = () => {
      if (!selectedPaymentType) {
        setcardInputError('You must select a payment type');
        setIsCardTypeValid(false);
      } else {
        setcardInputError('');
        setIsCardTypeValid(true);
      }
    };

    if (!isPaymentInProgress) {
      checkWithinPaymentMinMax();
      checkCardType();
    }
  }, [
    debouncedPaymentInputValue,
    selectedPaymentType,
    paymentDetails,
    currency,
    dispatch,
    isPaymentInProgress,
    due,
    isPageReady,
  ]);

  const handleCardTypeChange = (event) => {
    const selectedPayment = paymentDetails.paymentTypes.find(
      (paymentType) => paymentType.code === event.target.value
    );

    if (selectedPayment) {
      dispatch(setSelectedPaymentType(selectedPayment));
    }
  };

  const onIframeClose = async () => {
    dispatch(setIsCardHolderDetailsOpen(false));
    setPaymentFormModal(false);
    setIsPaymentInProgress(true);
    await refreshBookingAfterPayment();
    setIsPaymentInProgress(false);
    setPaymentFormLoadCount(0);
  };

  const checkIframePayment = () => {
    const { location: { href } } = ref.current.contentWindow.document;
    const paymentIframe = paymentFormLoadCount;
    
    if (/\/payment-status(\?|\b)/.test(href)) {
      window.location.replace(href);
    }

    if (paymentIframe <= 1) {
      setPaymentFormLoadCount(paymentFormLoadCount + 1);
    }

    const loginFormEl =
      ref.current.contentWindow.document.getElementById('loginForm');
    if (loginFormEl) {
      setPaymentFormLoadCount(0);
      window.location.replace('/self-service/login');
    }
  };

  const getUrl = async (receipt, bookingRef, fileName) => {
    const typeParam = `type=${receipt.documentName} 1`;
    const paymentIdParam = `&paymentId=${receipt.receiptReference}`;
    const bookingRefParam = bookingRef ? `&reference=${bookingRef}` : '';
    const fileNameParam = fileName ? `&fileName=${fileName}` : '';
    const url =  `/api/self-service/document?${typeParam}${paymentIdParam}${bookingRefParam}${fileNameParam}`;
    window.open(url, '_blank');
  };

  return (
    <>
      <div className={!isMobile ? containerStyle : 'mt-[40px] text-center'}>
        <Button
          className={`${due === 0 && 'hidden'} ${accordionButtonStyle}`}
          onClick={() => toggleAccordionOpen()}
        >
          <span>Make a payment</span>
          <Chevron direction={chevronDirection} />
        </Button>
        
        {componentConfig.bookingReceiptsEnabled &&
          <div className="flex justify-center grow mt-6 md:justify-end lg:mt-0">
            <button
              type="button"
              className="bold underline font-medium"
              onClick={() => setPaymentHistoryModal(!isPaymentHistoryModalOpen)}
              style={{ color: primaryColour }}
            >
              Show full payment history
              {paymentHistory && paymentHistory.receiptsOnly === false && ' and schedule'}
            </button>
          </div>
        }

        <div className={`${due === 0 && 'hidden'} ${accordionStyle}`}>
          <div className="flex flex-col h-screen self-center md:flex-row md:h-auto md:self-auto">
            <div className="md:border-r md:pr-16 md:p-9 text-left p-[20px]">
              <button
                aria-label="Toggle accordion"
                className="rounded-full absolute top-0 right-0 mt-[20px] mr-[20px] p-3 md:hidden"
                type="button"
                onClick={() => toggleAccordionOpen()}
                style={{ background: primaryColour || DEFAULT_PRIMARY_COLOUR }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="#fff"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.58574 7.00001L0.635742 2.05001L2.04974 0.636013L6.99974 5.58601L11.9497 0.636013L13.3637 2.05001L8.41374 7.00001L13.3637 11.95L11.9497 13.364L6.99974 8.41401L2.04974 13.364L0.635742 11.95L5.58574 7.00001Z"
                    fill="#FFFFFF"
                  />
                </svg>
              </button>
              <p className="pb-10 block md:hidden" style={paymentDueStyling}>
                Make a payment
              </p>
              <p className="text-left">Total amount to pay</p>
              <p style={paymentDueStyling}>{displayAmount(due, currency)}</p>
            </div>
            <div className="flex flex-grow flex-wrap max-h-[1000vh] h-[500vh] md:max-h-auto md:h-auto">
              <div className="flex flex-col grow pt-8 w-11/12 md:w-auto md:py-9 text-left relative px-[20px]">
                {paymentDetails.paymentTypes?.length > 1 && (
                  <legend className="pb-2">Choose a payment type</legend>
                )}

                {paymentDetails.paymentTypes?.length > 1 &&
                  paymentDetails.paymentTypes.map((cardType) => {
                    const displayText = cardType.displayName?.trim() || cardType.receiptName;

                    return (
                      <div key={`${cardType.receiptName}-${cardType.code}`} className="flex items-center">
                        <input
                          type="radio"
                          id={`${cardType.receiptName}-${cardType.code}`}
                          name="paymentType"
                          value={cardType.code}
                          checked={selectedPaymentType?.code === cardType.code}
                          onChange={handleCardTypeChange}
                        />
                        <label className="ml-2" htmlFor={`${cardType.receiptName}-${cardType.code}`}>
                          {displayText}
                        </label>
                      </div>
                    );
                  })}
                {!isCardTypeValid && (
                  <div className="flex">
                    <p className="text-red-500">{cardInputError}</p>
                  </div>
                )}
                <label className="pt-2 pb-4 relative" htmlFor="payment">
                  How much would you like to pay?
                  <div className="relative">
                    <span className="absolute inset-y-0 mt-4 left-4 flex items-center text-gray-500">
                      {getCurrencySymbol(currency)}
                    </span>
                    <input
                      className="w-full mt-4 pl-12 pr-4 border h-[50px]"
                      type="text"
                      id="payment"
                      name="payment"
                      placeholder={paymentDetails?.maximumPayment?.toFixed(2)}
                      value={paymentInputValue}
                      onChange={handlePaymentInputChange}
                    />

                    <p
                      className={`flex items-center absolute mt-[10px] text-sm ${
                        isPaymentInputValid ? '' : 'text-red-500'
                      }`}
                    >
                      <CircleIcon
                        className="mr-2"
                        iconPadding="11px"
                        iconClassName="text-sm"
                      >
                        <MdInfo style={{ fill: primaryColour }} />
                      </CircleIcon>
                      {paymentInputError}
                    </p>
                  </div>
                </label>

                {isTablet && 
                  <NextPaymentDates 
                    nextDepositDate={paymentDetails?.nextDepositDate}
                    balanceDueDate={balanceDueDate}
                    depositLate={paymentDetails?.depositLate}
                  />
                }

              </div>
              <div className="flex flex-wrap items-end w-full md:w-auto flex-col justify-end">
                <div className="border-b p-2 border-[#CECECE] relative block md:hidden w-full md:w-auto" />
                <Button
                  className="md:self-end m-4 mb-[15px] min-w-[200px] w-[90%] md:w-auto md:ml-4 md:mr-9 md:mb-[55px]"
                  onClick={() => toggleCardHolderDetailsForm()}
                  disabled={!isPaymentInputValid || !isCardTypeValid}
                >
                  <span>Submit payment</span>
                </Button>
              </div>
            </div>
          </div>
          
          {!isTablet && 
            <NextPaymentDates
              nextDepositDate={paymentDetails?.nextDepositDate}
              balanceDueDate={balanceDueDate}
              depositLate={paymentDetails?.depositLate}
            />
          }

        </div>
        {isPaymentFormModalOpen}
        <Modal
          show={isCardHolderDetailsOpen}
          onClose={onIframeClose}
          worldPaymentActive={paymentFormLoadCount > 1}
          header={getHeaderText()}
          classNames="overflow-auto max-h-[750px]"
          body={
            isCardHolderDetailsOpen ? (
              <div>
                <iframe
                  src="/self-service/payment-form"
                  title="Payment iframe"
                  width="100%"
                  className="overflow-hidden"
                  allowFullScreen
                  ref={ref}
                  style={{ minHeight: '600px' }}
                  id="paymentForm"
                  onLoad={() => checkIframePayment()}
                />
              </div>
            ) : (
              <div className="w-full h-[400px] pt-[150px]">
                {textConfig?.paymentLoadingText && (
                  <div className="text-center text-gray-600 mb-4">
                    {textConfig.paymentLoadingText}
                  </div>
                )}
                <Spinner />
              </div>
            )
          }
        />
      </div>

      <Modal
        show={isPaymentHistoryModalOpen}
        onClose={() => setPaymentHistoryModal(false)}
        header={`Payment history ${paymentHistory && paymentHistory.receiptsOnly === false ? 'and schedule' : ''}`}
        classNames="h-[95%] xl:w-[95%] m-[2.5%]"
        modalBodyCss="overflow-auto relative !p-0 xl:mt-[25px]"
        modalDialogCss="w-full max-w-[1140px] md:w-[50%] xl:w-full"
        body={
          isPageReady ? (
            paymentHistory && (
              <table className="table-fixed w-full">
                <thead className="hidden xl:table-header-group sticky top-0 bg-white">
                  <tr className="w-full flex justify-between text-sm text-gray-500 border-b pb-3">
                    <th className={`${colCss} ${!isXL ? "pl-6" : ""}`}>Date</th>
                    <th className={colCss}/>
                    {!paymentHistory?.receiptsOnly && (
                      <th className={colCss}>Owed</th>
                    )}
                    <th className={colCss}>Received</th>
                    <th className={colCss}/>
                  </tr>
                </thead>
                <tbody>
                  
                  {isXL && !paymentHistory?.receiptsOnly && (
                    <tr className="flex flex-col w-full justify-between p-6 py-3 border-b xl:flex-row xl:justify-start xl:p-0 xl:py-3">
                      <td className={`${colCss} ${tdCss} text-center`}>
                        <span>Total owed: <br /> {displayAmount(paymentHistory.bookingTotal, currency)}</span>
                        <span>Total received: <br /> {displayAmount(paymentHistory.bookingTotalPaid, currency)}</span>
                      </td>
                    </tr>
                  )}

                  {paymentHistory.transactionRecords.map(
                    transactionRecord => (
                      <tr
                        className={`flex flex-col w-full justify-between p-6 py-3 border-b xl:flex-row xl:p-0 xl:py-3
                          ${transactionRecord.status === "PAID" && "bg-green-50"} 
                          ${transactionRecord.status === "OVERDUE" && "bg-rose-100"}`}
                        key={transactionRecord.date}
                      >
                        <td
                          className={`${colCss} ${!isXL ? "pl-6" : ""} ${tdCss}`}
                        >
                          {isXL && (
                            <span className="text-gray-500">Date</span>
                          )}
                          <span className="xl:w-full">
                            {moment(transactionRecord.date.substring(0, 10)).format(dateFormat)}
                          </span>
                        </td>

                        <td
                          className={`${colCss} ${tdCss}`}
                        >
                          {isXL && (
                            <span className="text-gray-500">Transaction</span>
                          )}
                          <span className="xl:w-full">{transactionRecord.transactionName || '-'}</span>
                        </td>

                        {!paymentHistory?.receiptsOnly && (
                          <td
                            className={`${colCss} ${tdCss}`}
                          >
                            {isXL && !transactionRecord.receipt && (
                              <span className="text-gray-500">Amount</span>
                            )}
                            <span className="xl:w-full">
                              {!transactionRecord.receipt && displayAmount(transactionRecord.amount, currency)}
                            </span>
                          </td>
                        )}

                        <td
                          className={`${colCss} ${tdCss}`}
                        >
                          {isXL && transactionRecord.receipt && (
                            <span className="text-gray-500">Amount</span>
                          )}
                          <span className="xl:w-full">
                            {transactionRecord.receipt && displayAmount(transactionRecord.amount, currency)}
                          </span>
                        </td>

                        <td
                          className={`${colCss} ${tdStatusDocs} ${transactionRecord.receipt ? "!justify-end xl:!justify-start" : ""}`}
                        >
                          {paymentHistory.documentsAvailable && transactionRecord.receipt && !transactionRecord.assetPayment ? (
                            <button
                              className="flex items-center text-sm"
                              onClick={() => getUrl(transactionRecord, reference, null)}
                              type="button"
                            >
                              Download receipt
                              <MdOutlineFileDownload style={{ marginLeft: "4px", fontSize: "20px" }} />
                            </button>
                          ) : (
                            transactionRecord.status && transactionRecord.status !== "DUE" && (
                              <div className={`
                                ${transactionRecord.status === "OVERDUE" ? "bg-red-600" : ""}
                                ${transactionRecord.status === "PAID" ? "bg-lime-600" : ""}
                                flex w-full items-center justify-center text-xs text-white px-4 py-1 rounded-full xl:w-auto xl:mr-4
                              `}>
                                {transactionRecord.status === "OVERDUE" && (
                                  <MdInfo style={{ fill: "white", marginRight: "4px" }} />
                                )}
                                {transactionRecord.status === "PAID" && (
                                  <FaCheckCircle style={{ fill: "white", marginRight: "4px" }} />
                                )}
                                {transactionRecord.status !== "PAID" ? `${displayAmount(transactionRecord.amountOwed, currency)} ` : ""}
                                {transactionRecord.status.toLowerCase().replace(/^\w/, c => c.toUpperCase())}
                              </div>
                            )
                          )}
                        </td>
                      </tr>
                    ),
                  )}

                  {!paymentHistory?.receiptsOnly && (
                    <tr className="hidden xl:flex w-full justify-between text-sm text-gray-500 border-b pt-3 pb-3">
                      <th className={`${colCss} ${!isXL ? "pl-6" : ""}`}>Total</th>
                      <th className={colCss}/>
                      <th className={colCss}>{displayAmount(paymentHistory.bookingTotal, currency)}</th>
                      <th className={colCss}>{displayAmount(paymentHistory.bookingTotalPaid, currency)}</th>
                      <th className={colCss}/>
                    </tr>
                  )}
                </tbody>
              </table>
            )) : (
              <div className="w-full h-[400px] pt-[150px]">
                <Spinner/>
              </div>
          )
        }
      />
    </>
  );
}
